import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { PostTopicListWithBreadcrumbs_TopicFragment } from '../../graphql/generated/graphql';
import { useAllTopicMap } from '../../topic/useAllTopics';
import { UIActions } from '../../ui/uiSlice';
import populateObjects from '../../utils/populateObjects';
import { isAddOrShareMenuOpenAtom } from '../controlBar/controlBarMenuAtoms';
import getRemoveTopicDisabledMessage from '../sharing/getRemoveTopicDisabledMessage';
import {
  useAddTopicToPost,
  useRemoveTopicFromPost,
} from '../sharing/postPermissionOperations';
import PostTopicListWithBreadcrumbs from './PostTopicListWithBreadcrumbs';
import getPostTopicToast, { POST_TOPIC_TOAST_TAG } from './getPostTopicToast';

const POST_FRAGMENT = gql(`
  fragment PostTopicBar_post on Post {
    id
    publishedAt
    topics {
      id
    }
    ...getRemoveTopicDisabledMessage_post
  }
`);

const SERIES_FRAGMENT = gql(`
  fragment PostTopicBar_series on SeriesFields {
    id
    ...PostTopicListWithBreadcrumbs_postSeries
  }
`);

const EDIT_POST_QUERY = gql(/** @complexity 5 */ `
  query PostTopicBarEditing($id: ID!) {
    post(id: $id) {
      id
      ...useRemoveTopicFromPost_post
    }
  }
`);

type EventHandlerTopic = Parameters<typeof getPostTopicToast>[0]['topic'];

interface PostTopicBarProps {
  editing: boolean;
  post: DocumentType<typeof POST_FRAGMENT>;
  series?: DocumentType<typeof SERIES_FRAGMENT> | null;
}

const PostTopicBar = ({ post, series, editing }: PostTopicBarProps) => {
  const allTopicMap = useAllTopicMap();
  const postTopics = populateObjects(
    allTopicMap,
    post.topics.map(({ id }) => id),
  );
  const user = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();
  const organizationName = useAppSelector(
    (state) => state.session.organization.name,
  );

  const { data } = useQuery(EDIT_POST_QUERY, {
    skip: !editing,
    variables: { id: post.id },
  });
  const postForEditing = data?.post;

  const removeTopic = useRemoveTopicFromPost();
  const addTopic = useAddTopicToPost();

  const dismissToasts = useCallback(
    () => dispatch(UIActions.removeToastsByTag(POST_TOPIC_TOAST_TAG)),
    [dispatch],
  );

  const isPublished = !!post.publishedAt;

  const getWarningCallback = (topic: EventHandlerTopic, adding: boolean) => {
    const postTopicToast = getPostTopicToast({
      adding,
      addTopic: ({ id: topicId }) => {
        addTopic(post.id, topicId);
      },
      organizationName,
      postTopics,
      published: isPublished,
      removeTopic: (t) => postForEditing && removeTopic(postForEditing, t),
      topic,
    });

    if (postTopicToast) {
      dismissToasts();
      dispatch(
        UIActions.pushToast(postTopicToast, {
          tag: POST_TOPIC_TOAST_TAG,
        }),
      );
    }
  };

  useEffect(
    () => () => {
      dismissToasts();
    },
    [dismissToasts],
  );

  const setIsAddOrShareMenuOpen = useSetAtom(isAddOrShareMenuOpenAtom);

  const handleRemove = async (
    topic: PostTopicListWithBreadcrumbs_TopicFragment,
  ) => {
    if (postForEditing) {
      await removeTopic(postForEditing, topic);

      if (postForEditing.topics.length === 1) {
        setIsAddOrShareMenuOpen(true);
      }
    }
    getWarningCallback(topic, false);
  };

  const isEditing = editing && !!postForEditing;

  if (postTopics.length === 0 && !series) {
    return null;
  }

  return (
    <PostTopicListWithBreadcrumbs
      postId={post.id}
      postTopics={postTopics}
      postSeries={series}
      {...(isEditing
        ? {
            editing: true,
            onRemove: handleRemove,
            getRemoveTopicDisabledMessage: (topic) => {
              const fullTopic = allTopicMap[topic.id];
              if (!fullTopic) return undefined;
              return getRemoveTopicDisabledMessage(fullTopic, user, post);
            },
          }
        : { editing: false })}
    />
  );
};

export default PostTopicBar;
